/**
 * Plan/pricing copy that appears on more than one surface.
 *
 * Kept here so the same claim cannot drift between the public pricing page and
 * the in-app billing page. Follows the same rule as `aesCopy.ts`: the words a
 * customer reads about what a plan does are written once.
 */

/**
 * Footnote for the `*` on every "SMS / WhatsApp delivery" row.
 *
 * Three surfaces carry that asterisk, and only two of them live in this repo:
 *   1. public pricing — plan cards (all viewports) + compare-plans matrix (desktop only)
 *   2. in-app billing — plan comparison
 *   3. the Pro plan card bullet, which comes from `plan_definitions.features_display`
 *      in the DATABASE. Changing the wording here does NOT change that one — it is
 *      set by an Alembic data migration and editable at /admin/plans.
 *
 * This is a disclosure, not an enforced limit: as of 2026-09-07 `sms_delivery` is a
 * boolean entitlement with no volume cap and no destination restriction anywhere in
 * the backend. It reserves the right to impose one. If a real cap ships later, check
 * that this wording still matches what the code actually does.
 */
export const SMS_FOOTNOTE =
  "* SMS / WhatsApp delivery is subject to fair-use limits. High-cost destinations may be rate-limited or unavailable.";

/**
 * The plan numbers static marketing pages quote — written ONCE.
 *
 * ⚠️ NOT the source of truth: production `GET /api/billing/plans` is (prices and
 * limits live in `plan_definitions`). Static SEO pages can't fetch at render time
 * without slowing down the pages Google measures, so they read these constants, and
 * `tests/unit/marketingClaims.test.ts` fails the build when any public page pairs a
 * plan name with a price that is not in here. That is how "Business ($29/mo)" — a
 * price we never charged — sat on /free-esignature: the number was typed into the
 * page. When a plan price changes, change it here AND verify against the API.
 *
 * Verified against production /api/billing/plans on 2026-10-08.
 */
export const PLAN_FACTS = {
  free: { monthly: 0, sendsPerMonth: 10, recipientsPerEnvelope: 3, aiDocsPerMonth: 3 },
  pro: { monthly: 15, annual: 150, sendsPerMonth: 25 },
  business: { monthly: 80, annual: 800, sendsPerMonth: 100 },
} as const;

/**
 * What the developer surface costs — Path A (owner, 2026-10-08): the code gives
 * EVERY plan live API keys, webhooks, the MCP server and bulk-sign sessions, with no
 * per-plan API quota (the old "5 / 100 / 500 API envelopes a month" was UI copy that
 * nothing enforced). API sends count toward the plan's monthly envelopes like any
 * other send; sandbox (test-key) envelopes don't. Never quote a per-plan API number.
 */
export const API_PLAN_LINE =
  "Live API, webhooks and MCP server on every plan, including Free. API sends count toward your plan's monthly envelopes; sandbox (test-key) envelopes don't.";
